<template>
    <aside class="patient-sidebar">
        <div class="sidebar-section">
            <p class="sidebar-heading">PATIENT RECORD</p>

            <div v-for="item in patientItems" :key="item.key" class="sidebar-item-wrapper">
                <div class="sidebar-item" :class="getPatientItemClass(item)" @click="openTab(item)">
                    <div class="sidebar-item__left">
                        <common-icon :name="item.icon" size="small"></common-icon>
                        <span>{{ item.label }}</span>
                    </div>

                    <common-icon
                        name="leftarrow"
                        size="tiny"
                        class="sidebar-item__arrow"
                        :class="getItemArrowClass(item.key)"
                    ></common-icon>
                </div>
                <div v-if="isItemDisabled(item)" class="sidebar-tooltip">
                    {{ getItemTooltip(item) }}
                </div>
            </div>
        </div>

        <div class="sidebar-section">
            <p class="sidebar-heading">ACTIONS</p>

            <div v-for="item in actionItems" :key="item.key" class="sidebar-item-wrapper">
                <template v-if="item.children && item.children.length">
                    <div
                        class="sidebar-item sidebar-item--action sidebar-item--dropdown"
                        :class="getDropdownItemClass(item)"
                        @click="toggleDropdown(item)"
                    >
                        <div class="sidebar-item__left">
                            <common-icon :name="item.icon" size="small"></common-icon>
                            <span>{{ item.label }}</span>
                        </div>

                        <common-icon
                            name="dropdown"
                            size="tiny"
                            class="sidebar-item__dropdown-icon"
                            :class="getDropdownIconClass(item)"
                        ></common-icon>
                    </div>

                    <transition name="sidebar-submenu">
                        <div v-show="isDropdownExpanded(item)" class="sidebar-submenu">
                            <div
                                v-for="subItem in item.children"
                                :key="subItem.key"
                                class="sidebar-subitem"
                                :class="getSubitemClass(subItem, item)"
                                @click="openTab(subItem)"
                            >
                                <div class="sidebar-subitem__left">
                                    <common-icon :name="subItem.icon" size="tiny"></common-icon>
                                    <span>{{ subItem.label }}</span>
                                </div>

                                <common-icon
                                    name="leftarrow"
                                    size="tiny"
                                    class="sidebar-item__arrow"
                                    :class="getItemArrowClass(subItem.key)"
                                ></common-icon>
                            </div>
                        </div>
                    </transition>
                </template>

                <template v-else>
                    <div class="sidebar-item sidebar-item--action" :class="getActionItemClass(item)" @click="openTab(item)">
                        <div class="sidebar-item__left">
                            <common-icon :name="item.icon" size="small"></common-icon>
                            <span>{{ item.label }}</span>
                        </div>

                        <common-icon
                            name="leftarrow"
                            size="tiny"
                            class="sidebar-item__arrow"
                            :class="getItemArrowClass(item.key)"
                        ></common-icon>
                    </div>
                </template>

                <div v-if="isItemDisabled(item)" class="sidebar-tooltip">
                    {{ getItemTooltip(item) }}
                </div>
            </div>
        </div>
    </aside>
</template>

<script>
    export default {
        props: {
            activeTab: {
                type: String,
                default: 'profile',
            },
            patientId: {
                type: [String, Number],
                default: null,
            },
        },
        data() {
            return {
                expandedDropdowns: {},
            }
        },
        computed: {
            hasPatient() {
                return !!(this.patientId || this.$route.params.id)
            },

            patientItems() {
                return [
                    {
                        key: 'profile',
                        label: this.$t('modals.patient.tab.label_three'),
                        icon: 'user',
                    },
                    {
                        key: 'appointments',
                        label: this.$t('modals.patient.tab.label_one'),
                        icon: 'timeline',
                    },
                    {
                        key: 'documents',
                        label: this.$t('modals.patient.tab.label_two'),
                        icon: 'file',
                    },
                ]
            },

            actionItems() {
                return [
                    {
                        key: 'request-records',
                        label: this.$t('modals.patient_profile.profile.detail_form.tabs.request'),
                        icon: 'record',
                    },
                    {
                        key: 'record-requests-inbox',
                        label: this.$t('modals.patient_profile.profile.detail_form.tabs.request_inbox'),
                        icon: 'sms',
                        isDisabled: true,
                        tooltip: this.$t('modals.patient.feature_coming_soon'),
                    },
                    {
                        key: 'hipaa-forms',
                        label: this.$t('modals.patient_profile.profile.detail_form.tabs.hipaa'),
                        icon: 'insure',
                    },
                    {
                        key: 'intake-forms',
                        label: this.$t('modals.patient_profile.profile.detail_form.tabs.intake.item'),
                        icon: 'document',
                        children: [
                            {
                                key: 'general-intake',
                                label: this.$t('modals.patient_profile.profile.detail_form.tabs.intake.subitem.general'),
                                icon: 'document',
                            },
                            {
                                key: 'accident-intake',
                                label: this.$t('modals.patient_profile.profile.detail_form.tabs.intake.subitem.accident'),
                                icon: 'file',
                            },
                        ],
                    },
                    {
                        key: 'refer-patient',
                        label: this.$t('modals.refer_patient.buttons.refer'),
                        icon: 'add-user',
                    },
                    {
                        key: 'message-patient',
                        label: this.$t('modals.sidebarList.content.message'),
                        icon: 'chat',
                    },
                    {
                        key: 'delete-patient',
                        label: this.$t('modals.sidebarList.content.remove'),
                        icon: 'trash-bin',
                    },
                ]
            },
        },
        methods: {
            getPatientItemClass(item) {
                return {
                    'sidebar-item--active': this.activeTab === item.key,
                    'sidebar-item--disabled': this.isItemDisabled(item),
                }
            },
            getItemArrowClass(key) {
                return {
                    'sidebar-item__arrow--active': this.activeTab === key,
                }
            },
            getDropdownItemClass(item) {
                return {
                    'sidebar-item--active': this.isParentActive(item),
                    'sidebar-item--expanded': this.isDropdownExpanded(item),
                    'sidebar-item--disabled': this.isItemDisabled(item),
                }
            },
            getDropdownIconClass(item) {
                return {
                    'sidebar-item__dropdown-icon--open': this.isDropdownExpanded(item),
                }
            },
            getSubitemClass(subItem, parentItem) {
                return {
                    'sidebar-subitem--active': this.activeTab === subItem.key,
                    'sidebar-subitem--disabled': this.isItemDisabled(parentItem),
                }
            },
            getActionItemClass(item) {
                return {
                    'sidebar-item--active': this.activeTab === item.key,
                    'sidebar-item--delete': item.key === 'delete-patient',
                    'sidebar-item--disabled': this.isItemDisabled(item),
                }
            },
            isItemDisabled(item) {
                if (!this.hasPatient && item.key !== 'profile') {
                    return true
                }
                return !!item.isDisabled
            },
            getItemTooltip(item) {
                if (!this.hasPatient && item.key !== 'profile') {
                    return 'Please save patient profile first'
                }
                return item.tooltip || this.$t('modals.patient.feature_coming_soon')
            },
            isParentActive(item) {
                if (this.activeTab === item.key) return true
                if (item.children) {
                    return item.children.some((child) => child.key === this.activeTab)
                }
                return false
            },
            isDropdownExpanded(item) {
                return !!this.expandedDropdowns[item.key]
            },
            toggleDropdown(item) {
                if (this.isItemDisabled(item)) return
                const willOpen = !this.expandedDropdowns[item.key]
                this.$set(this.expandedDropdowns, item.key, willOpen)

                if (willOpen && item.children && item.children.length) {
                    const isChildActive = item.children.some((child) => child.key === this.activeTab)
                    if (!isChildActive) {
                        this.openTab(item.children[0])
                    }
                }
            },
            openTab(item) {
                const key = typeof item === 'object' ? item.key : item
                if (this.isItemDisabled(item)) {
                    return
                }
                this.$emit('toggle', key)
            },
        },
        watch: {
            activeTab: {
                immediate: true,
                handler(newTab) {
                    this.actionItems.forEach((item) => {
                        if (item.children) {
                            if (item.key === newTab || item.children.some((c) => c.key === newTab)) {
                                this.$set(this.expandedDropdowns, item.key, true)
                            }
                        }
                    })
                },
            },
        },
    }
</script>
<style scoped lang="scss">
    @import 'styles/setup/_variables.scss';
    @import 'styles/setup/_mixins.scss';
    .patient-sidebar {
        width: 260px;
        background: #fff;
        border: 1px solid #eceff3;
        border-radius: 12px;
        padding: 20px 0;

        display: flex;
        flex-direction: column;

        height: auto;
        align-self: stretch;
    }

    .sidebar-section {
        display: flex;
        flex-direction: column;
    }
    .sidebar-section + .sidebar-section {
        padding-top: 12px;
    }

    .sidebar-heading {
        padding: 0 30px;
        color: #99a1af;
        font-size: 12px;
        font-weight: 500;
        letter-spacing: 0.08em;
        text-transform: uppercase;
    }

    .sidebar-item-wrapper {
        position: relative;

        &:hover .sidebar-tooltip {
            visibility: visible;
            opacity: 1;
            transform: translateY(-50%) scale(1);
        }
    }

    .sidebar-item {
        display: flex;
        align-items: center;
        justify-content: space-between;

        margin: 2px 8px;
        padding: 12px 20px;

        border-radius: 10px;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease;

        color: #4a5565;
        font-size: 14px;
        font-weight: 500;

        &:hover {
            background: #f9fafb;
            color: #111827;

            i,
            .sidebar-item__arrow {
                color: #111827;
            }
        }
    }

    .sidebar-item--disabled {
        cursor: not-allowed !important;
        opacity: 0.55;
        color: #94a3b8 !important;

        i {
            color: #94a3b8 !important;
        }

        &:hover {
            background: transparent !important;
            color: #94a3b8 !important;

            i,
            .sidebar-item__arrow {
                color: #94a3b8 !important;
            }
        }
    }

    .sidebar-item__left {
        display: flex;
        align-items: center;
        gap: 12px;

        i {
            color: #99a1af;
            transition: color 0.2s ease;
        }
    }

    .sidebar-item__arrow {
        opacity: 0;
        color: inherit;
        transition: opacity 0.2s ease;
    }

    .sidebar-item__arrow--active {
        opacity: 1;
    }

    .sidebar-item--active:not(.sidebar-item--delete) {
        background: #eff6ff;
        color: #1447e6;
        font-weight: 500;

        i {
            color: #1447e6;
        }
    }
    .sidebar-item--delete {
        color: #fb2c36;

        i {
            color: #fb2c36;
        }

        &:hover {
            background: #fef2f2;
            color: #b91c1c;

            i,
            .sidebar-item__arrow {
                color: #b91c1c;
            }
        }
    }
    .sidebar-item--delete.sidebar-item--active {
        background: #fef2f2;
        color: #e7000b;

        i,
        .sidebar-item__arrow {
            color: #e7000b;
        }
    }

    .sidebar-item__dropdown-icon {
        color: #99a1af;
        transition: transform 0.2s ease, color 0.2s ease;
        font-size: 10px;

        &--open {
            transform: rotate(180deg);
            color: #1447e6;
        }
    }

    .sidebar-submenu {
        display: flex;
        flex-direction: column;
        margin: 2px 8px 6px;
        padding-left: 12px;
        border-left: 2px solid #e2e8f0;
        gap: 2px;
    }

    .sidebar-subitem {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px 12px 8px 14px;
        border-radius: 8px;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease;
        color: #64748b;
        font-size: 13px;
        font-weight: 500;

        &:hover {
            background: #f8fafc;
            color: #0f172a;

            i,
            .sidebar-item__arrow {
                color: #0f172a;
            }
        }

        &--active {
            background: #eff6ff;
            color: #1447e6;
            font-weight: 500;

            i {
                color: #1447e6;
            }
        }

        &--disabled {
            cursor: not-allowed !important;
            opacity: 0.55;
            color: #94a3b8 !important;
        }

        &__left {
            display: flex;
            align-items: center;
            gap: 10px;

            i {
                color: inherit;
            }
        }
    }

    .sidebar-submenu-enter-active,
    .sidebar-submenu-leave-active {
        transition: all 0.2s ease-out;
        overflow: hidden;
    }

    .sidebar-submenu-enter,
    .sidebar-submenu-leave-to {
        opacity: 0;
        max-height: 0;
        transform: translateY(-4px);
    }

    .sidebar-tooltip {
        visibility: hidden;
        opacity: 0;
        position: absolute;
        left: calc(100% + 10px);
        top: 50%;
        transform: translateY(-50%) scale(0.95);
        background-color: #1e293b;
        color: #f8fafc;
        padding: 6px 12px;
        border-radius: 6px;
        font-size: 12px;
        font-weight: 400;
        white-space: nowrap;
        pointer-events: none;
        transition: opacity 0.2s ease, transform 0.2s ease, visibility 0.2s ease;
        z-index: 1000;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1);

        &::after {
            content: '';
            position: absolute;
            top: 50%;
            right: 100%;
            margin-top: -5px;
            border-width: 5px;
            border-style: solid;
            border-color: transparent #1e293b transparent transparent;
        }
    }

    @media (max-width: 992px) {
        .patient-sidebar {
            width: 100%;
        }

        .sidebar-tooltip {
            left: 50%;
            top: auto;
            bottom: calc(100% + 6px);
            transform: translateX(-50%) scale(0.95);

            &::after {
                top: 100%;
                left: 50%;
                right: auto;
                margin-top: 0;
                margin-left: -5px;
                border-color: #1e293b transparent transparent transparent;
            }
        }

        .sidebar-item-wrapper:hover .sidebar-tooltip {
            transform: translateX(-50%) scale(1);
        }
    }
</style>
